/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { Play, Clock, ChevronRight, BookOpen, Calendar, Users, CreditCard, Settings, FileText, BarChart3, Sparkles, ExternalLink, Lock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const TutorialVideos = () => {
  const { userType } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('getting-started');
  const [playingVideo, setPlayingVideo] = useState(null);

  const eoCategories = [
    { id: 'getting-started', name: 'Getting Started', icon: BookOpen },
    { id: 'events', name: 'Event Management', icon: Calendar },
    { id: 'vendors', name: 'Vendor Management', icon: Users },
    { id: 'payments', name: 'Payments & Billing', icon: CreditCard },
    { id: 'analytics', name: 'Analytics', icon: BarChart3 },
    { id: 'settings', name: 'Settings & Profile', icon: Settings },
  ];

  const mpsCategories = [
    { id: 'getting-started', name: 'Getting Started', icon: BookOpen },
    { id: 'events', name: 'Finding Events', icon: Calendar },
    { id: 'applications', name: 'Applications', icon: FileText },
    { id: 'payments', name: 'Payments', icon: CreditCard },
    { id: 'settings', name: 'Settings & Profile', icon: Settings },
  ];

  const categories = userType === 'EO' ? eoCategories : mpsCategories;

  const eoVideos = {
    'getting-started': [
      {
        id: 'eo-welcome',
        title: 'Your Event Business Starts Here',
        description: 'Post your first event and start collecting vendor registrations in under 5 minutes.',
        duration: '2:30',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
      },
      {
        id: 'eo-profile-setup',
        title: 'Create Your Organizer Profile',
        description: 'Set up your organizer page so vendors can find and trust your events.',
        duration: '3:45',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
      },
      {
        id: 'eo-stripe-connect',
        title: 'Connect Your Bank Account',
        description: 'Link your payment account so vendor fees go straight to you — securely and automatically.',
        duration: '4:15',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
      },
    ],
    'events': [
      {
        id: 'eo-create-event',
        title: 'Post Your Event',
        description: 'Create your market, fair, or festival listing and set your vendor booth fees.',
        duration: '5:20',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
        featured: true,
      },
      {
        id: 'eo-ai-event',
        title: 'AI Quick Create',
        description: 'Describe your event in a sentence and let Pebble build the listing for you.',
        duration: '2:45',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
      },
      {
        id: 'eo-manage-events',
        title: 'Run Multiple Events',
        description: 'Copy past events, manage multiple listings, and keep everything organized.',
        duration: '4:00',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
      },
    ],
    'vendors': [
      {
        id: 'eo-review-apps',
        title: 'Approve Vendors Fast',
        description: 'See who wants to join your event. Approve with one tap — they pay, you earn.',
        duration: '4:30',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
        featured: true,
      },
      {
        id: 'eo-vendor-docs',
        title: 'Collect Required Documents',
        description: 'Require permits, insurance, or licenses before vendors can register for your event.',
        duration: '3:15',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
      },
      {
        id: 'eo-vendor-crm',
        title: 'Grow Your Vendor List',
        description: 'Save your favorite vendors and invite them back to future events automatically.',
        duration: '6:00',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
        isPro: true,
      },
    ],
    'payments': [
      {
        id: 'eo-payment-flow',
        title: 'Collect Booth Fees',
        description: 'When you approve a vendor, they pay instantly. Your cut hits your bank automatically.',
        duration: '3:30',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
      },
      {
        id: 'eo-receipts',
        title: 'See Your Earnings',
        description: 'Track every payment you collect. Download reports for your records.',
        duration: '2:15',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
      },
    ],
    'analytics': [
      {
        id: 'eo-analytics-overview',
        title: 'Event Performance',
        description: 'See how many vendors applied, how much you earned, and which events did best.',
        duration: '5:00',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
        isPro: true,
      },
      {
        id: 'eo-revenue-tracking',
        title: 'Revenue Reports',
        description: 'Track your total earnings across all events — by week, month, or year.',
        duration: '3:45',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
        isPro: true,
      },
    ],
    'settings': [
      {
        id: 'eo-account-settings',
        title: 'Account Settings',
        description: 'Update your info, change your plan, or manage your payout settings.',
        duration: '2:30',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
      },
      {
        id: 'eo-notifications',
        title: 'Get Notified',
        description: 'Know instantly when a vendor applies or pays — never miss a registration.',
        duration: '1:45',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
      },
    ],
  };

  const mpsVideos = {
    'getting-started': [
      {
        id: 'mps-welcome',
        title: 'Welcome to Pebble',
        description: 'Get started as a marketplace seller on Pebble.',
        duration: '2:30',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
      },
      {
        id: 'mps-profile-setup',
        title: 'Setting Up Your Profile',
        description: 'Create an attractive vendor profile to stand out.',
        duration: '4:00',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
      },
    ],
    'events': [
      {
        id: 'mps-browse-events',
        title: 'Browsing Events',
        description: 'Learn how to find and filter events that match your business.',
        duration: '3:15',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
        featured: true,
      },
      {
        id: 'mps-event-details',
        title: 'Understanding Event Details',
        description: 'What to look for in event listings before applying.',
        duration: '2:45',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
      },
    ],
    'applications': [
      {
        id: 'mps-apply',
        title: 'Applying to Events',
        description: 'Step-by-step guide to submitting applications.',
        duration: '4:30',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
        featured: true,
      },
      {
        id: 'mps-track-apps',
        title: 'Tracking Your Applications',
        description: 'Monitor your application status and next steps.',
        duration: '2:00',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
      },
      {
        id: 'mps-documents',
        title: 'Managing Documents',
        description: 'Upload and manage your business documents.',
        duration: '3:30',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
      },
    ],
    'payments': [
      {
        id: 'mps-payment-process',
        title: 'Payment Process',
        description: 'How to pay registration fees securely.',
        duration: '3:00',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
      },
      {
        id: 'mps-receipts',
        title: 'Accessing Receipts',
        description: 'Download receipts for your records.',
        duration: '1:45',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
      },
    ],
    'settings': [
      {
        id: 'mps-account-settings',
        title: 'Account Settings',
        description: 'Update your account and business information.',
        duration: '2:30',
        thumbnail: null,
        videoUrl: null,
        comingSoon: true,
      },
    ],
  };

  const videos = userType === 'EO' ? eoVideos : mpsVideos;
  const currentVideos = videos[selectedCategory] || [];
  
  const eoIntro = "Post events, collect booth fees, and manage your vendors — all in one place. Simple. Quick. Secure.";
  const mpsIntro = "Learn how to find events, apply as a vendor, and grow your business with our video guides.";

  const VideoCard = ({ video }) => (
    <div 
      className={`bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-all cursor-pointer group ${
        video.comingSoon ? 'opacity-75' : ''
      }`}
      onClick={() => !video.comingSoon && video.videoUrl && setPlayingVideo(video)}
    >
      <div className="relative aspect-video bg-gradient-to-br from-gray-100 to-gray-200">
        {video.thumbnail ? (
          <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
              video.comingSoon 
                ? 'bg-gray-300' 
                : 'bg-pebble-red group-hover:bg-red-600 transition-colors'
            }`}>
              <Play className={`h-6 w-6 ${video.comingSoon ? 'text-gray-500' : 'text-white'} ml-1`} />
            </div>
          </div>
        )}
        
        {video.comingSoon && (
          <div className="absolute top-2 left-2 px-2 py-1 bg-gray-800/80 text-white text-xs font-medium rounded-full">
            Coming Soon
          </div>
        )}
        
        {video.featured && !video.comingSoon && (
          <div className="absolute top-2 left-2 px-2 py-1 bg-pebble-red text-white text-xs font-medium rounded-full flex items-center gap-1">
            <Sparkles className="h-3 w-3" />
            Featured
          </div>
        )}
        
        {video.isPro && (
          <div className="absolute top-2 right-2 px-2 py-1 bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-xs font-medium rounded-full">
            PRO
          </div>
        )}
        
        <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 text-white text-xs rounded flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {video.duration}
        </div>
      </div>
      
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-pebble-red transition-colors">
          {video.title}
        </h3>
        <p className="text-sm text-gray-600 line-clamp-2">{video.description}</p>
      </div>
    </div>
  );

  if (userType === 'MPS') {
    return (
      <div className="p-6 max-w-2xl mx-auto flex flex-col items-center justify-center min-h-[60vh] text-center">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center mb-6">
          <Lock className="h-8 w-8 text-white" />
        </div>
        <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-xs font-semibold rounded-full mb-4">
          PRO FEATURE
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-3">Tutorials are coming to Pro</h1>
        <p className="text-gray-500 mb-8 max-w-md leading-relaxed">
          Step-by-step video guides will be available with the Pebble Pro plan for vendors. We're building it out now — stay tuned.
        </p>
        <div className="w-full max-w-sm bg-gray-50 rounded-2xl border border-gray-200 p-5 text-left space-y-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">What's coming</p>
          {['Getting started as a vendor', 'How to find & apply to events', 'Managing your documents', 'Tracking applications & payments'].map((item) => (
            <div key={item} className="flex items-center gap-2.5 text-sm text-gray-600">
              <div className="w-4 h-4 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-2.5 h-2.5 text-purple-500" />
              </div>
              {item}
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-6">
          Questions? <a href="/mps/support" className="text-pebble-red underline underline-offset-2">Contact support</a>
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">How Pebble Works</h1>
        <p className="text-gray-600">{userType === 'EO' ? eoIntro : mpsIntro}</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="lg:w-56 flex-shrink-0">
          <nav className="space-y-1">
            {categories.map((category) => {
              const IconComponent = category.icon;
              const isActive = selectedCategory === category.id;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    isActive 
                      ? 'bg-red-50 text-pebble-red font-medium' 
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <IconComponent className={`h-5 w-5 ${isActive ? 'text-pebble-red' : 'text-gray-400'}`} />
                  {category.name}
                </button>
              );
            })}
          </nav>

          <div className="mt-6 p-4 bg-gradient-to-br from-red-50 to-orange-50 rounded-xl border border-red-100">
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-pebble-red" />
              Need More Help?
            </h4>
            <p className="text-sm text-gray-600 mb-3">
              Check out our FAQs or contact support for personalized assistance.
            </p>
            <a 
              href="/faqs" 
              className="inline-flex items-center gap-1 text-sm font-medium text-pebble-red hover:text-red-600"
            >
              View FAQs
              <ChevronRight className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div className="flex-1">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {categories.find(c => c.id === selectedCategory)?.name}
            </h2>
            <p className="text-sm text-gray-500">{currentVideos.length} videos</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {currentVideos.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>

          {currentVideos.length === 0 && (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">No videos yet</h3>
              <p className="text-gray-500">Tutorials for this category are coming soon.</p>
            </div>
          )}
        </div>
      </div>

      {playingVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="relative w-full max-w-4xl mx-4">
            <button
              onClick={() => setPlayingVideo(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              Close
            </button>
            <div className="aspect-video bg-black rounded-xl overflow-hidden">
              {playingVideo.videoUrl ? (
                <iframe
                  src={playingVideo.videoUrl}
                  title={playingVideo.title}
                  className="w-full h-full"
                  allowFullScreen
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white">
                  Video coming soon
                </div>
              )}
            </div>
            <div className="mt-4 text-white">
              <h3 className="text-xl font-semibold">{playingVideo.title}</h3>
              <p className="text-gray-300 mt-1">{playingVideo.description}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TutorialVideos;
